<div #mapContainer class="map">
  <google-map
    (mapInitialized)="onMapInitialized($event)"
    height="100%"
    width="100%"
    [options]="mapOptions()"
    (mapClick)="closeInfoWindows()"
    [class.grayscale]="input().grayscale"
    (zoomChanged)="updateSvgPathStrokeWidth()"
    (mapDrag)="onMapDrag()"
    (idle)="onIdle()"
  >
    <!--
      One shared info window for every marker, mirroring the polygon path below: an
      `InfoWindow` can only show one anchor at a time, so N of them bought nothing but N SDK
      objects. It is declared above the `@for` and reached from TypeScript through
      `markerInfoWindow()` — a template ref would be out of scope inside a sibling `@if`.
    -->
    @if (hasMarkerInfoWindow()) {
      <map-info-window
        #markerInfoWindow="mapInfoWindow"
        [options]="{ pixelOffset: markerInfoWindowOffset() }"
      ></map-info-window>
    }

    <!--
      `markerViews()` rather than `markers()`: the badge symbol is derived once per payload in a
      computed, because a `getMarkerSymbol(...)` call here would return a fresh object on every
      change-detection pass and keep `pozi-symbol`'s required input dirty once per marker per pass.
    -->
    @for (markerView of markerViews(); track $index) {
      <div style="display: none">
        <!--
          `.disabled` turns off `pointer-events` on the marker (see the stylesheet). It keys off
          the component-level template now, not a per-marker one: with nothing to open, a click
          would do nothing anyway, and no consumer ever set the template for only *some* markers.
        -->
        <div #markerContent [class.disabled]="!infoWindowTemplate()">
          <button class="marker-content">
            @if (markerView.marker.icon) {
              <pozi-icon [icon]="markerView.marker.icon!"></pozi-icon>
            } @else {
              <pozi-symbol [matSymbol]="markerView.symbol"></pozi-symbol>
            }
          </button>
          @if (markerView.marker.label) {
            <span class="pin-label">{{ markerView.marker.label }}</span>
          }
        </div>

        <div #infoWindowContent class="info-window-content">
          @if (infoWindowTemplate()) {
            <ng-container
              [ngTemplateOutlet]="infoWindowTemplate()!"
              [ngTemplateOutletContext]="{ $implicit: markerView.marker }"
            ></ng-container>
          }
        </div>
      </div>
      <map-advanced-marker
        #advancedMarker="mapAdvancedMarker"
        [position]="markerView.marker.position"
        [content]="markerContent"
        (mapClick)="openInfoWindow(advancedMarker, infoWindowContent)"
      />
    }

    @if (!!polygons().length && polygonInfoWindowTemplate()) {
      <map-info-window #polygonInfoWindow="mapInfoWindow"></map-info-window>
    }

    @for (polygon of polygons(); track $index) {
      <map-polygon
        [paths]="polygon.path"
        [options]="{
          clickable: !!polygonInfoWindowTemplate(),
          draggable: false,
          strokeColor: polygon.strokeColor ?? defaultPolygonStrokeColor,
          strokeWeight: polygon.strokeWeight ?? defaultPolygonStrokeWeight,
          fillColor: polygon.fillColor ?? defaultPolygonFillColor,
          fillOpacity: polygon.fillOpacity ?? defaultPolygonFillOpacity,
          zIndex: polygon.zIndex ?? $index,
        }"
        (polygonClick)="openPolygonInfoWindow($event, polygonInfoWindowContent)"
      />

      <div style="display: none">
        <div #polygonInfoWindowContent class="info-window-content polygon-info">
          @if (polygonInfoWindowTemplate()) {
            <ng-container
              [ngTemplateOutlet]="polygonInfoWindowTemplate()!"
              [ngTemplateOutletContext]="{ polygon, polygonInfoWindow }"
            ></ng-container>
          }
        </div>
      </div>
    }

    @for (polyline of polylines(); track $index) {
      <map-polyline
        [options]="{
          clickable: false,
          draggable: false,
          strokeWeight: polyline.strokeWeight || 2,
          strokeColor: polyline.strokeColor || '#14181C',
        }"
        [path]="polyline.positions"
      />
    }
  </google-map>

  <div class="controls-container" [class.visible]="mapReady()">
    <!-- TIMELINE -->
    @if (
      input().timeline && input().timeline?.amChart && isTimelineDisplayed()
    ) {
      <div class="bottom-center">
        @if (isTimelineChartInputV2(input().timeline!)) {
          <pozi-timeline-chart-v2
            [data]="timelineV2()"
            [showHideToggle]="true"
            (amChartMouseOver)="setMarkerFromTimeline($event)"
            (sizeChanged)="timelineHeight.set($event)"
            (closed)="isTimelineDisplayed.set(false)"
          ></pozi-timeline-chart-v2>
        } @else {
          <pozi-timeline-chart
            [data]="timelineV1()"
            (amChartMouseOver)="setMarkerFromTimeline($event)"
          ></pozi-timeline-chart>
        }
      </div>
    }

    <!-- BACK TO OVERVIEW btn -->
    @if (this.isBackToOverviewButtonEnabled()) {
      <div class="top-mid-controls">
        @if (freeRoam() && !isDrawingsEmpty()) {
          <button
            type="button"
            (click)="onBackToOverviewButtonClick()"
            [class.blurred]="isBlurred()"
          >
            <i class="material-symbols-rounded">zoom_out_map</i>
            <span i18n>Back to overview</span>
          </button>
        }
      </div>
    }

    <!-- CONTROLS -->
    @if (controlTemplates()) {
      @for (position of controlPositionEntries(); track position.key) {
        <div
          class="controls"
          [class]="position.key"
          [style.--timeline-height]="bottomControlsOffset()"
          [class.moved-up]="
            position.key.includes('bottom') &&
            input().timeline &&
            input().timeline?.amChart &&
            isTimelineDisplayed()
          "
        >
          @for (control of position.controls; track $index) {
            <ng-container
              [ngTemplateOutlet]="controlTemplates()![control]"
            ></ng-container>
          }
        </div>
      }
    }
  </div>
</div>

<!--
  The nine control templates live in this child, which renders no DOM of its own. It is placed
  *below* the `.controls` corners that consume them, which is why the parent's `viewChild()` for
  it must not be `.required()` — on the first pass the query is empty and `controlTemplates()` is
  `null`, exactly as the old `ngAfterViewInit` assignment was.
-->
<pozi-map-control-templates [host]="self" />
